import "./init";
import {
  StrictMode,
  Suspense,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApolloProvider } from "@apollo/client";
import { EmbedThemeProvider } from "@hl/base-components/lib/theme/provider";
import { AuthProvider } from "@hl/shared-features/lib/features/auth";
import { EmbedAppBootstrap } from "@hl/shared-features/lib/features/auth/AppBootstrap";
import { SwapModalProvider } from "@hl/shared-features/lib/features/balance/SwapModalContext";
import TransactionProvider from "@hl/shared-features/lib/features/evm-tx/TransactionProvider";
import { EmbedTheme } from "@hl/shared-features/lib/features/mint/types";
import { ModalStack } from "@hl/shared-features/lib/features/modal";
import { WatchlistAndFollowContextProvider } from "@hl/shared-features/lib/features/watchlist-and-follow/WatchlistAndFollowContext";
import WebsocketMqttConnection from "@hl/shared-features/lib/features/websocket/WebsocketMqttConnection";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { debounce, pick } from "lodash";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

import MintPage from "~features/MintPage";
import { LiveMintingEmbed } from "~features/MintPage/live-minting/LiveMintingEmbed";

import {
  MintCardEmbed,
  EMBED_CARD_STYLE_NAMES,
} from "./features/MintPage/EmbedCard";
import { EmbedConnectWallet } from "./features/MintPage/embed/ConnectWallet";
import {
  EmbedFlags,
  EmbedFlagsContext,
  embedFlagsFromAttributes,
} from "./features/MintPage/embed/customize";
import { generateMantineTheme } from "./features/MintPage/embed/theme";
import { BitstreamSeedRefreshEmbed } from "./features/MintPage/seed-refresh/BitstreamSeedRefresh";
import Root, { MintCardEmbedModals } from "./features/layout/Root";
import { createEmbedApolloClient } from "./services/apolloClient";

try {
  TimeAgo.addDefaultLocale(en);
} catch (err) {
  console.warn("time ago init error", err);
  // ignore error, as site can have TimeAgo initialized already
}

type EmbedProps = {
  flags: EmbedFlags;
  customerEmbedId: string;
  theme?: Record<string, unknown>;
};

type EmbedAppProps = EmbedProps & {
  collectionId: string;
};

const EmbedApp: React.FC<EmbedAppProps> = ({ collectionId, flags }) => {
  // TODO: https://linear.app/highlight-xyz/issue/ENG-3667/analytics-for-mint-embeds

  return (
    <Suspense fallback={""}>
      <EmbedFlagsContext.Provider value={flags}>
        <EmbedThemeProvider enableDarkMode={flags.enableDarkMode}>
          <AuthProvider>
            <EmbedAppBootstrap>
              <TransactionProvider>
                <SwapModalProvider>
                  <ModalStack>
                    <WatchlistAndFollowContextProvider>
                      <Root>
                        <MintPage embedCollectionId={collectionId} isEmbed />
                      </Root>
                    </WatchlistAndFollowContextProvider>
                  </ModalStack>
                </SwapModalProvider>
              </TransactionProvider>
            </EmbedAppBootstrap>
          </AuthProvider>
        </EmbedThemeProvider>
      </EmbedFlagsContext.Provider>
    </Suspense>
  );
};

type EmbedCardProps = EmbedProps & {
  collectionId: string;
  classNames?: Record<string, unknown>;
  styles?: Record<string, unknown>;
  containerHtml?: string;
};

const EmbedCardStandalone: React.FC<EmbedCardProps> = ({
  theme,
  flags,
  ...props
}) => {
  const customTheme = useMemo(() => {
    if (!theme) {
      return;
    }
    return generateMantineTheme(theme as EmbedTheme, flags);
  }, [theme, flags]);
  return (
    <Suspense fallback={""}>
      <AuthProvider>
        <EmbedAppBootstrap>
          <EmbedFlagsContext.Provider value={flags}>
            <EmbedThemeProvider
              customTheme={customTheme}
              enableDarkMode={flags.enableDarkMode}
            >
              <EmbedCard {...props} />
            </EmbedThemeProvider>
          </EmbedFlagsContext.Provider>
        </EmbedAppBootstrap>
      </AuthProvider>
    </Suspense>
  );
};

const EmbedCard: React.FC<Omit<EmbedCardProps, "theme" | "flags">> = ({
  collectionId,
  ...other
}) => {
  // TODO: move TransactionProvider up to share by multiple cards with the same col id
  return (
    <TransactionProvider>
      <SwapModalProvider>
        <ModalStack>
          <WatchlistAndFollowContextProvider>
            <MintCardEmbed collectionId={collectionId} {...other} />
            <MintCardEmbedModals collectionId={collectionId} />
          </WatchlistAndFollowContextProvider>
        </ModalStack>
      </SwapModalProvider>
    </TransactionProvider>
  );
};

const BistreamSeedRefreshEmbedCard: React.FC<{ collectionId: string }> = ({
  collectionId,
}) => {
  // TODO: move TransactionProvider up to share by multiple cards with the same col id
  return (
    <TransactionProvider>
      <SwapModalProvider>
        <ModalStack>
          <WatchlistAndFollowContextProvider>
            <BitstreamSeedRefreshEmbed collectionId={collectionId} />
          </WatchlistAndFollowContextProvider>
        </ModalStack>
      </SwapModalProvider>
    </TransactionProvider>
  );
};

const LiveMintingEmbedCard: React.FC<{ collectionId: string }> = ({
  collectionId,
}) => {
  return (
    <WebsocketMqttConnection>
      <WatchlistAndFollowContextProvider>
        <LiveMintingEmbed collectionId={collectionId} />
      </WatchlistAndFollowContextProvider>
    </WebsocketMqttConnection>
  );
};

const renderPage = (container: Element, props: EmbedAppProps) => {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ApolloProvider client={createEmbedApolloClient(props.customerEmbedId)}>
        <EmbedApp {...props} />
      </ApolloProvider>
    </StrictMode>
  );
};

const renderCard = (container: Element, props: EmbedCardProps) => {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ApolloProvider client={createEmbedApolloClient(props.customerEmbedId)}>
        <EmbedCardStandalone {...props} containerHtml={container.innerHTML} />
      </ApolloProvider>
    </StrictMode>
  );
};

let stopRender = false;

const stringToObj = (input: string, attrName: string) => {
  if (!input) {
    return {};
  }
  const text = input
    // replace single quotes
    .replace(/'/g, '"')
    // ensure proper double quotes around property names
    .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');
  let json = {};
  try {
    json = JSON.parse(text);
  } catch (err) {
    console.error(`failed to parse JSON string in ${attrName}`, { err });
  }
  return json;
};

const getEmbedProps = (
  container: Element,
  embedType: "card" | "page" | "other"
): EmbedAppProps | EmbedCardProps => {
  const readObjAttribute = (attrName: string, allowedProps?: string[]) => {
    const attrVal = container.getAttribute(attrName);
    let data;
    if (typeof attrVal === "string") {
      data = stringToObj(attrVal, attrName);
    } else if (typeof attrVal === "object") {
      data = attrVal;
    }
    if (data && allowedProps) {
      return pick(data, allowedProps);
    }
    return data;
  };

  return {
    collectionId: container.getAttribute("data-mint-collection-id") ?? "",
    customerEmbedId: container.getAttribute("data-customer-embed-id") ?? "beta",
    flags: embedFlagsFromAttributes(container) as EmbedFlags,
    theme: readObjAttribute("data-theme") ?? undefined,
    ...(embedType === "card" && {
      styles: readObjAttribute("data-styles", EMBED_CARD_STYLE_NAMES),
      classNames: readObjAttribute("data-class-names", EMBED_CARD_STYLE_NAMES),
    }),
  };
};

document
  .querySelectorAll(`[data-widget="highlight-mint-page"]`)
  .forEach((container) => {
    const props = getEmbedProps(container, "page");
    if (!stopRender && props.collectionId) {
      renderPage(container, props);
      // multiple HL pages instances are not supported
      stopRender = true;
    }
  });

// legacy flow, render each card individually, not using shared context
const loadCardsLegacy = () => {
  if (stopRender) {
    return;
  }

  document
    .querySelectorAll(`[data-widget="highlight-mint-card"]`)
    .forEach((container) => {
      const props = getEmbedProps(container, "card");
      const { collectionId } = props;
      if (collectionId) {
        if (container.getAttribute("data-rendered") === "true") {
          // already rendered, ignore
          return;
        }

        const placeholders = document.querySelectorAll(
          `[data-mint-collection-id="${collectionId}"]`
        );
        if (placeholders.length > 1 && placeholders[0] !== container) {
          // ignore all other placeholders with the same id
          // dynamically checking html to support live html editors
          // where embed code can be removed and added without page reload
          return;
        }
        renderCard(container, props);
        container.setAttribute("data-rendered", "true");
      }
    });
};

const Widget = memo(
  ({
    type,
    props: { flags, theme, ...props },
  }: Omit<EmbedWidget, "container">) => {
    const customTheme = useMemo(() => {
      if (!theme) {
        return;
      }
      return generateMantineTheme(theme as EmbedTheme, flags);
    }, [theme, flags]);

    let widget;
    if (type === WidgetType.MintCard) {
      widget = <EmbedCard {...props} />;
    } else if (type === WidgetType.ConnectWallet) {
      widget = <EmbedConnectWallet />;
    } else if (type === WidgetType.SeedRefreshButton) {
      widget = (
        <BistreamSeedRefreshEmbedCard collectionId={props.collectionId} />
      );
    } else if (type === WidgetType.LiveMinting) {
      widget = <LiveMintingEmbedCard collectionId={props.collectionId} />;
    } else {
      console.warn("invalid widget type", type);
      return null;
    }

    return (
      <EmbedFlagsContext.Provider value={flags}>
        <EmbedThemeProvider
          customTheme={customTheme}
          enableDarkMode={flags.enableDarkMode}
        >
          {widget}
        </EmbedThemeProvider>
      </EmbedFlagsContext.Provider>
    );
  }
);

const EmbedPortalsWrapper = () => {
  const widgets = useEmbedWidgets();

  if (!widgets.length) {
    return null;
  }
  const embedId = widgets[0].props.customerEmbedId;
  return (
    <Suspense fallback={""}>
      <ApolloProvider client={createEmbedApolloClient(embedId)}>
        <AuthProvider>
          <EmbedAppBootstrap>
            {widgets.map(({ container, type, props }, index) =>
              createPortal(
                <Widget type={type} props={props} />,
                container,
                index.toString()
              )
            )}
          </EmbedAppBootstrap>
        </AuthProvider>
      </ApolloProvider>
    </Suspense>
  );
};

enum WidgetType {
  MintCard = "mint-card",
  ConnectWallet = "connect-wallet",
  SeedRefreshButton = "seed-refresh-button",
  LiveMinting = "live-minting",
}

interface EmbedWidget {
  type: WidgetType;
  container: Element;
  props: EmbedCardProps;
}

export function useEmbedWidgets() {
  const [widgets, setWidgets] = useState<EmbedWidget[]>([]);

  useEffect(() => {
    const findWidgets = () => {
      const elements = document.querySelectorAll(
        Object.values(WidgetType)
          .map((type) => `[data-widget="highlight-${type}"]`)
          .join(", ")
      );
      const newWidgets = Array.from(elements)
        .map((container) => {
          const typeAttr = container
            .getAttribute("data-widget")
            ?.replace("highlight-", "");

          const type = typeAttr as WidgetType;
          if (!typeAttr || !Object.values(WidgetType).includes(type)) {
            console.warn("invalid widget type", typeAttr);
            return null;
          }

          const props = getEmbedProps(
            container,
            type === WidgetType.MintCard ? "card" : "other"
          ) as EmbedCardProps;
          return {
            type,
            container,
            props,
          };
        })
        .filter(Boolean) as EmbedWidget[];

      setWidgets((prevWidgets) => {
        if (areWidgetsEqual(prevWidgets, newWidgets)) {
          return prevWidgets;
        }
        return newWidgets;
      });
    };

    const debouncedFindWidgets = debounce(findWidgets, 100);

    // Initial search for containers
    findWidgets();

    if (!window.MutationObserver) {
      return;
    }

    // Set up MutationObserver
    const observer = new MutationObserver((mutationsList) => {
      const isAdded = mutationsList.some(
        (mutation) => mutation.addedNodes.length > 0
      );
      if (isAdded) {
        debouncedFindWidgets();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup function
    return () => {
      observer.disconnect();
      debouncedFindWidgets.cancel();
    };
  }, []);

  return widgets;
}

// Helper function to compare two arrays of widgets
function areWidgetsEqual(
  prevWidgets: EmbedWidget[],
  newWidgets: EmbedWidget[]
): boolean {
  if (prevWidgets.length !== newWidgets.length) {
    return false;
  }

  return prevWidgets.every((prevWidget, index) => {
    const newWidget = newWidgets[index];
    return (
      prevWidget.type === newWidget.type &&
      // prevWidget.container === newWidget.container &&
      JSON.stringify(prevWidget.props) === JSON.stringify(newWidget.props)
    );
  });
}

const renderWidgetPortals = () => {
  const rootId = "highlight-embed-root";
  let rootElement = document.getElementById(rootId);
  if (!rootElement) {
    rootElement = document.createElement("div");
    rootElement.id = rootId;
    document.body.appendChild(rootElement);
  }
  const root = createRoot(rootElement);
  root.render(<EmbedPortalsWrapper />);
};

function bootstrapCardsLegacy() {
  loadCardsLegacy();
  if (window.MutationObserver) {
    const loadCardsDebounced = debounce(loadCardsLegacy, 100);

    const callback: MutationCallback = (mutationList) => {
      const isAdded = mutationList.find(
        (mutation) => mutation.addedNodes.length
      );
      if (isAdded) {
        loadCardsDebounced();
      }
    };
    // watch for changes in document structure
    const observer = new MutationObserver(callback);
    const body = document.querySelector("body");
    if (body) {
      observer.observe(body, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }
  }
}

// Read the embed version from the data attribute of <script> tag
function getEmbedVersion(): number {
  const script = document.getElementById(
    "highlight-embed-script"
  ) as HTMLScriptElement | null;
  const versionString = script?.getAttribute("data-embed-version") || "1.0";
  return parseFloat(versionString);
}

const embedsVersion = getEmbedVersion();

if (!stopRender) {
  console.log("embed widgets mode", { embedsVersion });
  if (embedsVersion >= 2.0) {
    renderWidgetPortals();
  } else {
    // temporary keep legacy mode until 2.0 is fully tested
    bootstrapCardsLegacy();
  }
}
